/** A product or one of its matched listings, as the store locator receives it. */
export type OnlineListing = { retailerId?: unknown; matchedProducts?: OnlineListing[] | null };

/** The "Purchase online" row: the listing it shows, and each retailer that sells the product online. */
export interface PurchaseOnlineRow {
  product: OnlineListing;
  retailerIds: number[];
}

/** The "Purchase online" row of the store locator, or null when the locator shows no such row. */
export function purchaseOnlineRow(args: {
  product: OnlineListing | null | undefined;
  /** The retailers that sell the product online. `/store-locator/search` serves them for a V2 app only. */
  onlineRetailerIds: number[] | undefined;
  /** The app's own commerce retailers. */
  checkoutRetailerIds: number[] | undefined;
  /** False when the store turned the row off in its Modules settings. */
  showPurchaseOnline: boolean;
}): PurchaseOnlineRow | null {
  const { product, onlineRetailerIds, checkoutRetailerIds, showPurchaseOnline } = args;
  if (!showPurchaseOnline) return null;
  // V2: the served list decides. An empty list means that neither checkout nor link out is enabled.
  // V1 has no served list, so the app's own commerce retailers decide.
  // With no served list and no commerce retailers, every retailer of the product shows.
  const allowed = onlineRetailerIds
    ? new Set(onlineRetailerIds)
    : checkoutRetailerIds?.length
      ? new Set(checkoutRetailerIds)
      : undefined;
  const listings = [product, ...(product?.matchedProducts ?? [])].filter(
    (listing): listing is OnlineListing & { retailerId: number } =>
      !!listing && typeof listing.retailerId === 'number' && (!allowed || allowed.has(listing.retailerId)),
  );
  if (listings.length === 0) return null;
  return { product: listings[0], retailerIds: Array.from(new Set(listings.map((listing) => listing.retailerId))) };
}
